(ns hai.home.mobile.mini-apps.tasks
  "My Tasks mini-app card for the mobile dashboard: the same task data as the
   full Tasks page, grouped into collapsible Now / Next / Later sections. The ＋
   swaps the card into Add-a-task mode and a row swaps it for the task open in
   it; the title links to the full Tasks page. Shares the task row with the
   desktop card via [[hai.tasks.card]], the two views via
   [[hai.tasks.mini-app]] and the tinted header row via [[hai.tasks.header]];
   the card frame stays mobile's own."
  (:require
   [hai.entity.data :as entity-data]
   [hai.entity.sections :as sections]
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as frame]
   [hai.spaces.data :as spaces-data]
   [hai.tasks.add-pill :as add-pill]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.ui.components.section-toggle :as section-toggle]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.routing :as routing]
   [humbleai.belt.set :as set]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; The frame stays mobile's own — softer shadow, 20px radius, touch-sized
;; targets — but the header row is the shared one every tasks surface uses.
(o/defstyled card mini-apps/card
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "20px"
     :border-bottom-right-radius "20px"}]]
  [header/bar
   {:border-top-left-radius "20px"
    :border-top-right-radius "20px"}
   ;; Touch targets.
   [:.bar-action-btn {:width "32px" :height "32px"}]
   [sections/kebab-wrap
    [:button {:width "32px" :height "32px"}]]
   ;; The tasks card wears the shared tasks header, not the mobile card frame's
   ;; own, so the frame's pill sizing doesn't reach it from here. The capsule's
   ;; height is all it takes: its halves stretch to fill it.
   [add-pill/pill
    {:height "38px"}]]
  ;; Rows float as spaced cards rather than a bordered list. The inset holds
  ;; them off the section edges.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "8px"
    :padding "4px 14px 16px"}]
  ;; The shared frame clips the last section to round the card's bottom; with
  ;; inset floating cards there is nothing to clip. Let it overflow instead.
  [mini-apps/section
   [:&:last-child
    {:overflow "visible"}
    ;; Without that clip a collapsed last section sits on the card's bottom edge
    ;; with nothing rounding it, so its hover fill rounds its own corners. Open,
    ;; the rows are what reach the edge and the toggle stays square.
    [section-toggle/section-toggle
     ["&:not(.open)"
      {:border-bottom-left-radius "20px"
       :border-bottom-right-radius "20px"}]]]]
  ;; The body under the header is one height in every mode — the list, Add a
  ;; task, a task open in the card, even empty or loading — so switching between
  ;; them doesn't move the page: a short list leaves room below it, and a long
  ;; list or form scrolls inside rather than stretching the card. The clip
  ;; rounds the scrollbar into the card's corners.
  [frame/list-clip
   {:height "480px"
    :border-bottom-left-radius "20px"
    :border-bottom-right-radius "20px"}])

;; Links the add/save toasts to the task. Home lists tasks rather than showing
;; one, so the link always has somewhere to go.
(def ^:private task-link
  {:href-fn #(routing/href :tasks/detail {:task/uuid %})
   :target routing/link-target})

(defn- section-view [{:keys [label tasks open? on-toggle]}]
  [mini-apps/section-view
   {:label label :item-count (count tasks) :open? open? :on-toggle on-toggle}
   (when (seq tasks)
     (into [:div.section-tasks]
           (map (fn [task]
                  ^{:key (:task/uuid task)}
                  [task-card/task-row-component task
                   {:on-open-entity entity-data/open-entity}]))
           tasks))])

(defn panel [{title :mini-app/title}]
  (r/with-let [!open (r/atom data/bucket-ids)
               toggle-section (fn [id] (swap! !open set/toggle-membership id))]
    (let [buckets @(<s [::data/buckets])
          loaded? @(<s [::data/loaded?])
          adding? @(<s [::data/composer-open?])
          editing-task @(<s [::data/editor-task])
          default-urgency @(<s [::data/default-urgency])
          collapsed? @(<s [::mini-apps-data/collapsed? :tasks])
          open @!open
          total (reduce + (map count (vals buckets)))]
      (cond
        adding?
        [card
         [tasks-mini-app/composer-view
          {:draft-id data/composer-draft-id
           :saving? (= :start @(<s [:fetch/state ::create]))
           :show-hint? false
           :on-save (fn [staged]
                      (>e [::data/create (assoc staged
                                                :request-id ::create
                                                :task-link task-link
                                                :success-fx [[:dispatch [::data/close-composer]]])])
                      (swap! !open conj (data/staged-bucket staged)))
           :on-close #(>e [::data/close-composer])}]]

        editing-task
        [card
         [tasks-mini-app/editing-view
          {:task editing-task
           :show-hint? false
           :spaces? @(<s [::spaces-data/enabled?])
           :on-open-entity entity-data/open-entity
           :task-link task-link}]]

        :else
        [card {:class (when collapsed? "collapsed")}
         [header/bar
          {:title title
           :href (routing/href :tasks/view)
           :target routing/link-target}
          [add-pill/component
           {:on-add (fn []
                      (>e [::mini-apps-data/expand :tasks])
                      (>e [::data/open-composer (form/from-task nil default-urgency)]))}]
          [frame/collapse-toggle
           {:collapsed? collapsed?
            :label      "tasks"
            :class      "bar-action-btn"
            :on-toggle  #(>e [::mini-apps-data/toggle-collapsed :tasks])}]]
         (when-not collapsed?
           [frame/list-clip
            (cond
              (not loaded?) nil
              (zero? total) [:div.app-placeholder "Nothing yet — add your first task above."]
              :else
              (into [tasks-mini-app/sections]
                    (map (fn [{:keys [id label]}]
                           ^{:key id}
                           [section-view {:label label
                                          :tasks (get buckets id)
                                          :open? (contains? open id)
                                          :on-toggle #(toggle-section id)}]))
                    data/bucket-tabs))])]))))
